import React, { useState, useRef, useEffect } from 'react';
import { Box, TableCell, TextField, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useStockCurrency } from '../../../../contexts/StockCurrencyContext';

interface EditableCellProps {
  value: number;
  displayValue: string;
  isEditable: boolean;
  isOverridden: boolean;
  onSave: (value: number | undefined) => void;
  type: 'currency' | 'percentage' | 'growthPercent' | 'peRatio';
  align?: 'left' | 'right' | 'center';
}

/**
 * A table cell that supports inline editing.
 * - Click to enter edit mode
 * - Press Enter or blur to save
 * - Press Escape to cancel
 * - Shows visual indicator when value is overridden
 *
 * Types:
 * - currency: Revenue in billions (e.g., 450B)
 * - percentage: Margin as percentage 0-100% (e.g., 25.5%)
 * - growthPercent: Growth rate that can be negative (e.g., -10% to +100%)
 * - peRatio: PE multiple as absolute value (e.g., 20.0)
 */
const EditableCell: React.FC<EditableCellProps> = ({
  value,
  displayValue,
  isEditable,
  isOverridden,
  onSave,
  type,
  align = 'right',
}) => {
  const theme = useTheme();
  const { currencySymbol } = useStockCurrency();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    if (!isEditable) return;

    // Convert value to user-friendly format for editing
    if (type === 'currency') {
      // Display in billions for editing (e.g., 450 for $450B)
      setEditValue((value / 1e9).toFixed(2));
    } else if (type === 'peRatio') {
      // Display as absolute value (e.g., 20.0)
      setEditValue(value.toFixed(1));
    } else {
      // Display as percentage (e.g., 25.5 for 25.5% or 10.0 for 10%)
      setEditValue((value * 100).toFixed(1));
    }
    setIsEditing(true);
  };

  const handleSave = () => {
    const parsed = parseFloat(editValue);

    if (isNaN(parsed)) {
      // Invalid input - revert to original
      setIsEditing(false);
      return;
    }

    let finalValue: number;

    if (type === 'currency') {
      // Convert from billions back to raw value
      finalValue = parsed * 1e9;
      // Validate: revenue must be positive
      if (finalValue <= 0) {
        setIsEditing(false);
        return;
      }
    } else if (type === 'percentage') {
      // Convert from percentage to decimal
      finalValue = parsed / 100;
      // Validate: margin must be between -20% and 100% (negative range
      // covers loss-makers whose attributable margin is below zero).
      if (finalValue < -0.2 || finalValue > 1) {
        setIsEditing(false);
        return;
      }
    } else if (type === 'peRatio') {
      // PE ratio is absolute value (e.g., 20.0)
      finalValue = parsed;
      // Validate: PE must be between 1 and 999
      if (finalValue < 1 || finalValue > 999) {
        setIsEditing(false);
        return;
      }
    } else {
      // growthPercent - can be negative or positive
      finalValue = parsed / 100;
      // Validate: growth rate must be reasonable (-100% to +1000%)
      if (finalValue < -1 || finalValue > 10) {
        setIsEditing(false);
        return;
      }
    }

    // Only save if the value actually changed (with tolerance for floating point)
    const tolerance = type === 'currency' ? 1e6 : 0.0001; // $1M tolerance for currency, 0.01% for percentage
    const valueChanged = Math.abs(finalValue - value) > tolerance;

    if (valueChanged) {
      onSave(finalValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  // Build combined styles for the cell
  const getCellSx = () => {
    const baseSx: Record<string, unknown> = {
      fontSize: '1.05rem',
      py: 1.25,
      px: 2,
      whiteSpace: 'nowrap',
      minWidth: 120,
    };

    if (isEditable) {
      baseSx.cursor = 'pointer';
      baseSx.position = 'relative';
      baseSx['&:hover'] = {
        backgroundColor:
          theme.palette.mode === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.05)',
      };
      baseSx['&:hover .edit-indicator'] = {
        opacity: 1,
      };
    }

    return baseSx;
  };

  if (isEditing) {
    return (
      <TableCell align={align} sx={{ padding: '4px 8px', minWidth: 120 }}>
        <TextField
          inputRef={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          size="small"
          variant="outlined"
          InputProps={{
            endAdornment: type === 'currency' ? 'B' : type === 'peRatio' ? 'x' : '%',
            startAdornment: type === 'currency' ? currencySymbol : null,
          }}
          sx={{
            width: '100%',
            minWidth: 120,
            '& .MuiOutlinedInput-root': {
              fontSize: '1.05rem',
              height: 32,
              '& fieldset': {
                borderColor: '#22c55e',
              },
              '&:hover fieldset': {
                borderColor: '#22c55e',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#22c55e',
              },
            },
            '& .MuiOutlinedInput-input': {
              textAlign: 'right',
              padding: '5px 10px',
            },
          }}
        />
      </TableCell>
    );
  }

  return (
    <TableCell
      align={align}
      onClick={handleStartEdit}
      sx={getCellSx()}
    >
      <Tooltip
        title={isEditable ? 'Click to edit' : ''}
        arrow
        placement="top"
        disableHoverListener={!isEditable}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: align === 'right' ? 'flex-end' : align === 'left' ? 'flex-start' : 'center',
            gap: 0.5,
          }}
        >
          {isEditable && (
            <EditOutlinedIcon
              className="edit-indicator"
              sx={{
                fontSize: 15,
                color: 'text.secondary',
                opacity: isOverridden ? 0.6 : 0,
                transition: 'opacity 150ms ease-in-out',
                mr: 1.25,
              }}
            />
          )}
          {displayValue}
        </Box>
      </Tooltip>
    </TableCell>
  );
};

export default EditableCell;
